import styled, { css, createGlobalStyle } from "styled-components"
import { FaCheck, FaCircle, FaChevronRight } from "react-icons/fa"

export const PopoverGlobalStyles = createGlobalStyle`
  .popover {
    border: 1px solid #D4D9DB;
    border-radius: 8px;
    box-shadow: 0px 5px 10px rgba(0, 35, 75, 0.15);
    max-width: 308px;
  }

  .bs-popover-bottom {
    margin-top: 20px;
  }
  
  .popover-body {
    padding: 16px 24px;
  }
  
  .popover .arrow::before {
    border-bottom-color: #D4D9DB;
  }
  
  .popover .arrow::after {
    border-bottom-color: white;
  }
`

const textButtonStyles = css`
  color: #0082ce;
  text-decoration: underline;
  display: inline;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`

export const Container = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  box-shadow: 0px 5px 10px rgba(0, 35, 75, 0.15);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  color: #486280;
  position: relative;
`

export const SuspendedContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const DisabledOverlay = styled.div`
  opacity: 0.25;
  pointer-events: none;
`

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

export const TitleSection = styled.div`
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 2px;
  display: inline-flex;
`

export const Title = styled.h2`
  color: #00234b;
  font-size: 20px;
  font-family: "Playfair Display", serif;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
`

export const AssociateHeader = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

export const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background: #979797;

  @media (max-width: 768px) {
    display: none;
  }
`

export const ProgressSection = styled.div`
  padding: 16px;
  background: #f4f7f8;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const LevelTracker = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px 0;
`

export const LevelItem = styled.div`
  flex: 1;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  background: ${props => (props.active ? "white" : "transparent")};
  border-radius: ${props => (props.active ? "8px" : "0")};
  box-shadow: ${props =>
    props.active ? "0px 2px 2px rgba(0, 0, 0, 0.10)" : "none"};
  border: ${props => (props.active ? "1px solid #0082CE" : "none")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.active ? "white" : "#f8f9fa")};
  }
`

export const LevelLabel = styled.span`
  font-weight: 500;
  text-align: center;
`

export const LevelInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

export const LevelIndicatorDot = styled(FaCircle)`
  color: #d5d7db;
  width: 5px;
  height: 5px;
`

export const LevelCompleteIcon = styled(FaCheck)`
  color: #0082ce;
  width: 10px;
  height: 10px;
  font-weight: 600;
`

export const LevelInProgressIcon = styled(FaCircle)`
  color: #0082ce;
  width: 5px;
  height: 5px;
`

export const LevelWarningIcon = styled.img`
  width: 12px;
  height: 12px;
`

export const LevelLockIcon = styled.img`
  width: 10px;
  height: 10px;
`

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => (props.completed ? "#D0E2FF" : "#D2DEE3")};
  border-radius: 64px;
  overflow: hidden;
  position: relative;
`

export const ProgressFill = styled.div`
  height: 100%;
  background: #0082ce;
  border-radius: 1000px;
  width: ${props => props.percentage}%;
`

export const ProgramInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

export const ProgramInfoLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
`

export const ProgramInfoLink = styled.a`
  color: #0082ce;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`

export const RequirementsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const RequirementCardStyled = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${props => (props.hasWarning ? "#DC994A" : "#D4D9DB")};
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const RequirementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const RequirementTitle = styled.h3`
  color: #00234b;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin: 0;
`

export const StatusBadgeSmall = styled.div`
  padding: 4px 12px;
  background: ${props => props.backgroundColor || "#E9EEF2"};
  border-radius: 32px;
  display: flex;
  align-items: center;
  gap: 4px;
`

export const StatusBadgeText = styled.span`
  color: ${props => props.textColor || "#404446"};
  font-weight: 500;
  line-height: 16px;
`

export const StatusBadgeCheckmark = styled(FaCheck)`
  color: ${props => props.color || "#2CA24B"};
  width: 10px;
  height: 10px;
  font-weight: 600;
`

export const StatusBadgeLock = styled.img`
  width: 16px;
  height: 16px;
`

export const RequirementDescription = styled.div`
  color: #486280;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  line-height: 15px;
  margin: 0;
`

export const TextButton = styled.button`
  ${textButtonStyles};
`

export const RequirementInfoBanner = styled.div`
  padding: 6px 12px;
  background: #fff8ec;
  border-radius: 8px;
  border: 1px solid #f3fafd;
  display: flex;
  align-items: center;
`

export const RequirementInfoText = styled.p`
  flex: 1;
  padding: 4px 0;
  color: #cb6e00;
  font-weight: 500;
  line-height: 15px;
  margin: 0;
`

export const RequirementDenialMessage = styled.div`
  display: flex;
  justify-content: flex-start;
`

export const RequirementActions = styled.div`
  padding-top: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
`

export const ActionButton = styled.button`
  height: 24px;
  padding: 0 12px;
  border: 1px solid #0082ce;
  background: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background: #f8f9fa;
    text-decoration: none;
  }

  &:active {
    outline: none;
    text-decoration: none;
  }

  &:visited {
    text-decoration: none;
  }

  &:focus {
    outline: none;
    text-decoration: none;
  }
`

export const ActionButtonText = styled.span`
  color: #0082ce;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`

export const PortalButton = styled.a`
  width: 100%;
  padding: 16px 24px;
  background: #0082ce;
  border: 1px solid #0082ce;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background: #006ba6;
  }

  &:active,
  &:visited {
    outline: none;
  }
`

export const PortalButtonText = styled.span`
  color: white;
  font-weight: 500;
  text-transform: uppercase;
`

export const AdditionalRequirementsSection = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #d4d9db;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

export const AdditionalRequirementsHeader = styled.div`
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${props => (props.$collapsible ? "pointer" : "default")};
`

export const AdditionalRequirementsTitle = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const AdditionalRequirementsIcon = styled(FaChevronRight)`
  width: 16px;
  height: 16px;
  color: #00234b;
  transition: transform 0.2s ease;
`

export const AdditionalRequirementsTitleText = styled.h3`
  color: #00234b;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  margin: 0;
`

export const AdditionalRequirementsCount = styled.span`
  padding: 4px 12px;
  background: #e3f5fb;
  border-radius: 32px;
  color: #0065d0;
  font-weight: 500;
  line-height: 16px;
`

export const AdditionalRequirementsBody = styled.div`
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const AdditionalRequirementsDescription = styled.p`
  color: #1f344b;
  font-size: 11px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
  padding: 8px 0;
`

export const AdditionalRequirementCard = styled.div`
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid ${props => (props.$hasWarning ? "#DC994A" : "#D4D9DB")};
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const AdditionalRequirementHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`

export const AdditionalRequirementIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

export const AdditionalRequirementCompleteIcon = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const AdditionalRequirementCheckmark = styled(StatusBadgeCheckmark)`
  width: 12px;
  height: 12px;
`

export const AdditionalRequirementTitle = styled.h4`
  flex: 1;
  color: #00234b;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  line-height: 18px;
  margin: 0;
`

export const AdditionalRequirementDescription = styled.p`
  padding: 4px 0;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
`

export const AdditionalRequirementContent = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  display: flex;
`

export const AdditionalRequirementActions = styled.div`
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  display: inline-flex;
`

export const AdditionalRequirementTextLink = styled(TextButton)`
  font-size: 12px;
  font-weight: 500;
`

export const LevelProgressBar = styled.div`
  width: 100%;
  padding: 6px 12px;
  background: white;
  box-shadow: 0px 1px 2px rgba(0, 35, 75, 0.05);
  border-radius: 8px;
  outline: 1px #d2dee3 solid;
  outline-offset: -1px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`

export const LevelProgressContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  flex-wrap: wrap;
  flex: 1;

  @media (max-width: 768px) {
    gap: 4px;
  }
`

export const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 2px;
  }
`

export const DateLabel = styled.span`
  color: #486280;
  font-size: 10px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  white-space: nowrap;
`

export const DateValue = styled.span`
  color: #00234b;
  font-size: 10px;
  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  white-space: nowrap;
`

export const DateDivider = styled.div`
  width: 1px;
  height: 10px;
  background: #979797;
  align-self: center;

  @media (max-width: 768px) {
    display: none;
  }
`

export const LevelProgressText = styled.div`
  color: #00234b;
  font-size: 10px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  word-wrap: break-word;
`

export const LevelProgressCount = styled.span`
  font-weight: 700;
`

export const MobileBreak = styled.br`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`

export const LevelProgressLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #0082ce;
  font-size: 10px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  line-height: 18px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  background: none;
  border: none;
  padding: 0;
`

// Popover Styles
export const PopoverContainer = styled.div`
  padding: 0;
  font-family: "Montserrat", sans-serif;
  width: 100%;
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
`

export const PopoverHeader = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

export const PopoverTitle = styled.div`
  flex: 1;
  color: #00234b;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  line-height: 20px;
  word-wrap: break-word;
`

export const PopoverCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
  }

  &:focus {
    outline: none;
  }
`

export const PopoverLevelSection = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  display: flex;
  padding-bottom: ${props => (props.isLastSection ? "21px" : "0")};
`

export const PopoverLevelTitle = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
  display: inline-flex;
  color: #00234b;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  line-height: 20px;
  word-wrap: break-word;
  flex: 1 1 0;
`

export const PopoverLevelContent = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
`

export const PopoverDateRow = styled.div`
  width: 260px;
  height: 20px;
  justify-content: space-between;
  align-items: flex-start;
  display: inline-flex;
`

export const PopoverDateLabel = styled.div`
  color: ${props =>
    props.isWarning ? "#D3180C" : props.isCompleted ? "#2CA24B" : "#00234B"};
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  line-height: 20px;
  word-wrap: break-word;
`

export const PopoverDateValue = styled.div`
  color: ${props =>
    props.isWarning ? "#D3180C" : props.isCompleted ? "#2CA24B" : "#00234B"};
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  line-height: 20px;
  word-wrap: break-word;
`

export const PopoverNotStarted = styled.div`
  width: 260px;
  height: 20px;
  justify-content: flex-start;
  align-items: flex-start;
  display: inline-flex;
  color: rgba(0, 35, 75, 0.5);
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  line-height: 22px;
  word-wrap: break-word;
`

export const PopoverDivider = styled.div`
  width: 260px;
  height: 0;
  outline: 1px #d4d9db solid;
  outline-offset: -0.5px;
`

export const PopoverButton = styled.a`
  align-self: stretch;
  height: 42px;
  padding: 16px 24px;
  background: #0082ce;
  outline: 1px #0082ce solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  display: inline-flex;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: #006ba6;
    outline-color: #006ba6;
  }
`

export const PopoverButtonText = styled.div`
  text-align: center;
  color: white;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  word-wrap: break-word;
`
